import React from "react";
// import ClipLoader from "react-spinners/ClipLoader";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import "react-toastify/dist/ReactToastify.css";
import { Button } from "../Button/Button";
import {
  HeaderSection,
  Cards,
  PageContainer,
  Type,
  MainContainer,
  NoProjects,
  LoaderContainer,
  ProjectDisplaySection,
  ListViewWrapper,
} from "./Projects.styles";
import ActionCard from "../ActionCard/ActionCard";
import ProjectTypeIcon from "../Icons/IconHome";
import { Color } from "../../styles/Colors";
import { projectsSelector, getProjects } from "../../redux";
import { useReadOnlyUser } from "../../hooks/useReadOnlyUser";
import { toast } from "react-toastify";
import { StyledToast } from "../CreateNewUser/CreateNewUser.styles";
import { formatAddress } from "../../utils";
import NoProjectsIcon from "../../assets/images/projects/NoProjectsFound.png";
import BeatLoader from "react-spinners/BeatLoader";
import { FileUpload } from "../FileUpload/FileUpload";
import { RESET_PROJECT_BY_ID } from "../../constants";
import { ViewToggle } from "../ListView/ViewToggle";
import ProjectListTable from "../ListView/ProjectListTable";

export const Projects = React.memo(() => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = React.useState(false);
  const { projects = [] } = useSelector(projectsSelector);
  const { companyId } = useParams();
  const isReadonlyUser = useReadOnlyUser();
  const { state } = useLocation();
  const { searchQuery = "" } = state || {};
  const [filteredProjects, setFilteredProjects] = React.useState([]);
  const [popUpOpen, setPopUpOpen] = React.useState(false);
  const [showCompleted, setShowCompleted] = React.useState(false);

  const [hasInitialized, setHasInitialized] = React.useState(false);

  const [viewType, setViewType] = React.useState<"tile" | "list">(
    () => (localStorage.getItem("viewType") as "tile" | "list") || "tile",
  );

  const totalProjects =
    Array.isArray(projects) && projects.length > 0 ? projects.length : 0;

  const navigateToCreateProjects = () => {
    navigate(`/home/create-project/${companyId}`);
  };
  const openPopUp = () => {
    setPopUpOpen(true);
  };

  const closePopUp = () => {
    setPopUpOpen(false);
  };

  const handleActionCard = ({ projectId }) => {
    dispatch({ type: RESET_PROJECT_BY_ID });
    navigate(`/home/project-details/${companyId}/${projectId}`);
  };

  const displayedProjects = filteredProjects.slice(0, 100);
  React.useEffect(() => {
    localStorage.setItem("viewType", viewType);
  }, [viewType]);

  // Reset initialization when companyId changes
  React.useEffect(() => {
    setHasInitialized(false);
  }, [companyId]);

  React.useEffect(() => {
    if (companyId && !hasInitialized) {
      setHasInitialized(true);
      setIsLoading(true);
      dispatch(getProjects({ companyId }))
        .then(({ payload }) => {
          setIsLoading(false);
          const { projects } = payload || {};
          if (!projects) {
            notifyError("Not able to get projects");
          }
        })
        .catch((error) => {
          console.error("Error fetching projects:", error);
          setIsLoading(false);
          notifyError("Failed to fetch projects");
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, hasInitialized]);

  React.useEffect(() => {
    const filtered = projects.filter((project) => {
      const { pName, pLoanId, addr, city, st, zip, compDate, pm } = project;
      const address = `${addr || ""} ${city || ""} ${st || ""} ${zip || ""}`;
      const searchLower = searchQuery?.toLowerCase() || "";
      const matchesSearch =
        pName?.toLowerCase().includes(searchLower) ||
        pLoanId?.toLowerCase().includes(searchLower) ||
        address?.toLowerCase().includes(searchLower) ||
        pm?.toLowerCase().includes(searchLower);

      const includeCompleted = showCompleted || !compDate;

      return matchesSearch && includeCompleted;
    });
    setFilteredProjects(filtered);
  }, [searchQuery, projects, showCompleted]);

  const notifyError = (message) =>
    toast.error(message, {
      position: "top-right",
      autoClose: 3000,
      hideProgressBar: true,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
      theme: "light",
    });

  return (
    <PageContainer>
      <MainContainer>
        <StyledToast />
        <HeaderSection>
          <Type>Projects</Type>
          <ProjectDisplaySection>
            <p>
              Displaying {filteredProjects.length} of {totalProjects}
            </p>
            <label>
              <input
                type="checkbox"
                checked={showCompleted}
                onChange={(e) => setShowCompleted(e.target.checked)}
              />
              Show Completed Projects
            </label>
          </ProjectDisplaySection>
          <div className="button-group">
            <ViewToggle viewType={viewType} setViewType={setViewType} />

            <Button onClick={openPopUp}>Projects Upload</Button>
            {popUpOpen && (
              <FileUpload
                open={popUpOpen}
                onClose={closePopUp}
                singleFile={false}
              />
            )}
            <Button
              hasStartIcon
              type="primary"
              onClick={navigateToCreateProjects}
              disabled={isReadonlyUser}
            >
              Create New Projects
            </Button>
          </div>
        </HeaderSection>
        {isLoading && (
          <LoaderContainer>
            <BeatLoader
              color={Color.darkBlue}
              loading
              margin={4}
              size={15}
              aria-label="Loading Beats"
              data-testid="loader"
            />
          </LoaderContainer>
        )}
        {viewType === "list" ? (
          <ListViewWrapper>
            {isLoading ? (
              <LoaderContainer>
                <BeatLoader
                  color={Color.darkBlue}
                  loading
                  margin={4}
                  size={15}
                />
              </LoaderContainer>
            ) : totalProjects > 0 ? (
              <ProjectListTable
                projects={displayedProjects}
                onRowClick={(projectId) => handleActionCard({ projectId })}
              />
            ) : (
              <NoProjects>
                <img src={NoProjectsIcon} alt="no-projects-found" />
                <div>No projects found</div>
              </NoProjects>
            )}
          </ListViewWrapper>
        ) : (
          <Cards>
            {isLoading ? (
              <LoaderContainer>
                <BeatLoader
                  color={Color.darkBlue}
                  loading
                  margin={4}
                  size={15}
                />
              </LoaderContainer>
            ) : totalProjects > 0 ? (
              displayedProjects.map((item, index) => {
                const {
                  city,
                  pName: projectName,
                  st: stateCode,
                  addr: stAddress,
                  pId: projectId,
                  zip,
                  compDate,
                  pLoanId: projectLoanIDNumber,
                  maxStories,
                  tlSqFoot,
                } = item || {};
                const address = formatAddress(
                  {
                    stAddress: stAddress || "",
                    city: city || "",
                    stateCode: stateCode ? stateCode.toUpperCase() : "",
                    zip: zip || "",
                  },
                  true,
                );
                return (
                  <ActionCard
                    srText={""}
                    href={"#"}
                    isLoading={false}
                    media={<ProjectTypeIcon pType={item?.pType} size={40} />}
                    mediaPosition={"start"}
                    onClick={() => {
                      handleActionCard({ projectId });
                    }}
                    showArrow={true}
                    isCompleted={compDate}
                    date={compDate}
                    projectId={projectLoanIDNumber ? projectLoanIDNumber : " "}
                    title={projectName}
                    key={index}
                    index={index}
                    stories={maxStories ? maxStories : "No"}
                    sqft={tlSqFoot ? tlSqFoot : 0}
                    // budget="475,000"
                  >
                    {address}
                  </ActionCard>
                );
              })
            ) : (
              <NoProjects>
                <img src={NoProjectsIcon} alt="no-projects-found" />
                <div>No projects found</div>
              </NoProjects>
            )}
          </Cards>
        )}
      </MainContainer>
    </PageContainer>
  );
});

export default Projects;
